import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';

const AttachmentPreview = ({ attachments, initialIndex, onClose, incidentId, logId }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentIndex, attachments.length]);

  const goToPrevious = () => {
    const isFirst = currentIndex === 0;
    const newIndex = isFirst ? attachments.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const isLast = currentIndex === attachments.length - 1;
    const newIndex = isLast ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const currentAttachment = attachments[currentIndex];
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  
  let attachmentUrl = '';
  if (currentAttachment.url) {
      attachmentUrl = currentAttachment.url;
  } else if (incidentId) {
      attachmentUrl = `${BACKEND_URL}/api/health-safety/incidents/${incidentId}/attachments/${currentAttachment.id}`;
  } else if (logId || currentAttachment.service_log_id) {
      const serviceLogId = logId || currentAttachment.service_log_id;
      attachmentUrl = `${BACKEND_URL}/api/service-logs/${serviceLogId}/attachments/${currentAttachment.id}`;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="relative bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900 truncate">
            {currentAttachment.filename || 'Attachment Preview'}
          </h3>
          <div className="flex items-center space-x-4">
            <a
              href={attachmentUrl}
              download={currentAttachment.filename}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
              title="Download"
            >
              <Download size={20} />
            </a>
            <button onClick={onClose} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow relative flex items-center justify-center bg-gray-100 overflow-hidden">
          {currentAttachment.content_type?.startsWith('image/') ? (
            <img src={attachmentUrl} alt={currentAttachment.filename} className="max-w-full max-h-full object-contain" />
          ) : currentAttachment.content_type === 'application/pdf' ? (
            <iframe src={attachmentUrl} title={currentAttachment.filename} className="w-full h-full" frameBorder="0"></iframe>
          ) : (
            <div className="text-center text-gray-600">
              <p>Preview not available for this file type.</p>
              <p className="text-sm">({currentAttachment.content_type})</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        {attachments.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-opacity"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-opacity"
            >
              <ChevronRight size={24} />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white text-sm px-3 py-1 rounded-full">
              {currentIndex + 1} / {attachments.length}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AttachmentPreview;